import React, { useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  alpha,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import { THEME_COLORS } from '../../shared/constants/theme';
import { getDashboardDarkSurfaceTokens } from '../../utils/dashboardSurfaceSx';
import { getDistributionSectionIconBoxSx } from '../../utils/distributionMobileLayout';
import { getBookingDrawerMetaChipSx, BOOKING_DRAWER_ACCENT } from '../../utils/bookingDrawerLayout';

const ACCENT = THEME_COLORS.PRIMARY;

function AllocationRow({ inventory, isDark, tokens, onEdit, onDelete }) {
  const rooms = inventory.allocated_rooms || 0;
  const soldOut = rooms === 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1.15,
        borderRadius: 1.5,
        backgroundColor: isDark ? tokens.chipBg : alpha(ACCENT, 0.04),
        border: `1px solid ${tokens.surfaceBorder}`,
        transition: 'border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          borderColor: alpha(ACCENT, isDark ? 0.32 : 0.28),
          transform: 'translateX(2px)',
          boxShadow: isDark ? `0 4px 12px rgba(0,0,0,0.2)` : `0 4px 12px ${alpha(ACCENT, 0.1)}`,
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.25,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: soldOut ? alpha('#ef4444', isDark ? 0.14 : 0.1) : alpha(ACCENT, isDark ? 0.14 : 0.12),
          color: soldOut ? '#f87171' : ACCENT,
          fontWeight: 800,
          fontSize: '0.85rem',
        }}
      >
        {rooms}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, mb: 0.25 }}>
          <MeetingRoomOutlinedIcon sx={{ fontSize: 15, color: tokens.subtleText, flexShrink: 0 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: '0.8125rem',
              color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={inventory.room_type_name}
          >
            {inventory.room_type_name}
          </Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: tokens.subtleText, fontSize: '0.7rem' }}>
          {soldOut ? 'Sold out on channel' : `${rooms} ${rooms === 1 ? 'room' : 'rooms'} allocated`}
          {inventory.is_active === false ? ' · Inactive' : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 0.35,
          flexShrink: 0,
          p: 0.25,
          borderRadius: 999,
          bgcolor: isDark ? alpha('#ffffff', 0.03) : alpha(ACCENT, 0.05),
          border: `1px solid ${tokens.surfaceBorder}`,
        }}
      >
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => onEdit(inventory)}
            sx={{ color: BOOKING_DRAWER_ACCENT, width: 30, height: 30, '&:hover': { bgcolor: alpha(ACCENT, 0.14) } }}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete permanently (blocked if mapping exists for this channel-room type)">
          <IconButton
            size="small"
            onClick={() => onDelete(inventory)}
            sx={{ color: 'error.main', width: 30, height: 30, '&:hover': { bgcolor: alpha('#ef4444', 0.12) } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

export default function ChannelInventoryCard({ group, isDark, onEdit, onDelete }) {
  const tokens = getDashboardDarkSurfaceTokens(isDark);
  const { channel, inventories } = group;

  const totalAllocated = useMemo(
    () => inventories.reduce((sum, inv) => sum + (inv.allocated_rooms || 0), 0),
    [inventories]
  );

  const activeCount = useMemo(
    () => inventories.filter((inv) => inv.is_active !== false).length,
    [inventories]
  );

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        maxHeight: 520,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        background: isDark ? tokens.surfaceGradient : THEME_COLORS.CARD_BACKGROUND_LIGHT,
        backgroundColor: isDark ? THEME_COLORS.CARD_BACKGROUND_DARK : THEME_COLORS.CARD_BACKGROUND_LIGHT,
        border: `1px solid ${alpha(ACCENT, isDark ? 0.22 : 0.16)}`,
        boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : '0 1px 3px rgba(0,0,0,0.06)',
        transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.28s ease, border-color 0.28s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          borderColor: alpha(ACCENT, isDark ? 0.42 : 0.32),
          boxShadow: isDark
            ? `0 16px 32px rgba(0,0,0,0.35), 0 0 0 1px ${alpha(ACCENT, 0.12)}`
            : `0 12px 24px ${alpha(ACCENT, 0.16)}`,
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: `linear-gradient(90deg, ${ACCENT} 0%, ${THEME_COLORS.PRIMARY_HOVER} 100%)`,
        },
      }}
    >
      <CardContent
        sx={{
          flex: 1,
          p: 2.25,
          pt: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.75,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
          <Box sx={getDistributionSectionIconBoxSx(ACCENT, false)}>
            <InventoryIcon sx={{ fontSize: 22, color: ACCENT }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0, pt: 0.15 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                lineHeight: 1.25,
                color: isDark ? THEME_COLORS.TEXT_PRIMARY_DARK : THEME_COLORS.TEXT_PRIMARY_LIGHT,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                mb: 0.75,
              }}
              title={channel.name}
            >
              {channel.name}
            </Typography>
            <Chip
              label={channel.type_display || channel.type}
              size="small"
              sx={{
                ...getBookingDrawerMetaChipSx(isDark),
                height: 22,
                fontSize: '0.68rem',
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>

          {channel.image_path?.trim() ? (
            <Box
              sx={{
                flexShrink: 0,
                width: 56,
                height: 56,
                p: 0.75,
                borderRadius: 1.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isDark ? tokens.chipBg : THEME_COLORS.CARD_BACKGROUND_LIGHT,
                border: `1px solid ${tokens.surfaceBorder}`,
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
              }}
            >
              <img
                src={channel.image_path}
                alt={channel.name}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          ) : null}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25, flexShrink: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: tokens.text, fontSize: '0.8125rem' }}
            >
              Room allocations
            </Typography>
            <Chip
              size="small"
              label={`${inventories.length} types · ${activeCount} active`}
              sx={{
                height: 22,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: alpha(ACCENT, isDark ? 0.1 : 0.08),
                color: ACCENT,
                border: `1px solid ${alpha(ACCENT, 0.22)}`,
              }}
            />
          </Stack>

          <Stack
            spacing={0.85}
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 6 },
              '&::-webkit-scrollbar-track': {
                backgroundColor: isDark ? tokens.chipBg : alpha(ACCENT, 0.05),
                borderRadius: 3,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(ACCENT, isDark ? 0.35 : 0.28),
                borderRadius: 3,
              },
            }}
          >
            {inventories.map((inventory) => (
              <AllocationRow
                key={inventory.id}
                inventory={inventory}
                isDark={isDark}
                tokens={tokens}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Stack>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 1.35,
            borderRadius: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: isDark
              ? `linear-gradient(90deg, ${alpha(ACCENT, 0.12)} 0%, ${alpha(ACCENT, 0.04)} 100%)`
              : `linear-gradient(90deg, ${alpha(ACCENT, 0.1)} 0%, ${alpha(ACCENT, 0.03)} 100%)`,
            border: `1px solid ${alpha(ACCENT, isDark ? 0.24 : 0.18)}`,
          }}
        >
          <Typography variant="body2" sx={{ color: tokens.subtleText, fontWeight: 600, fontSize: '0.8125rem' }}>
            Total allocated
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: ACCENT,
              fontSize: '1.25rem',
              lineHeight: 1,
              textShadow: isDark ? `0 0 20px ${alpha(ACCENT, 0.25)}` : 'none',
            }}
          >
            {totalAllocated}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}
